import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import { XotaBundleUpdateStep } from '@vi-iot/client/index';
import { FirmwareUpdateModalComponent } from '../../+installations/installation/firmware-update-modal/firmware-update-modal.component';
import { FirmwareUpdateActions } from './firmware-update.actions';
import { FirmwareUpdateStep } from './firmware-update.model';
import { FirmwareUpdateSelectors } from './firmware-update.selectors';

@Injectable({ providedIn: 'root' })
export class FirmwareUpdateStore {
  step$ = this.store.select(FirmwareUpdateSelectors.selectStep);
  bundleUpdateStep$ = this.store.select(FirmwareUpdateSelectors.selectBundleUpdateStep);
  overallProgress$ = this.store.select(FirmwareUpdateSelectors.selectOverallProgress);
  componentVerification$ = this.store.select(FirmwareUpdateSelectors.selectComponentVerification);
  errorExtendedPayload$ = this.store.select(FirmwareUpdateSelectors.selectErrorExtendedPayload);
  currentBundleVersion$ = this.store.select(FirmwareUpdateSelectors.selectCurrentBundleVersion);
  targetBundleVersion$ = this.store.select(FirmwareUpdateSelectors.selectTargetBundleVersion);
  isLoading$ = this.store.select(FirmwareUpdateSelectors.selectIsLoading);
  isFirmwareUpdateButtonAvailable$ = this.store.select(
    FirmwareUpdateSelectors.isFirmwareUpdateButtonAvailable()
  );
  progress$ = this.store.select(FirmwareUpdateSelectors.selectProgress);
  status$ = this.store.select(FirmwareUpdateSelectors.selectStatus);
  processType$ = this.store.select(FirmwareUpdateSelectors.selectProcessType);
  isInUpdateTimeblock$ = this.store.select(FirmwareUpdateSelectors.selectIsInUpdateTimeblock);
  isUpdateAvailable$ = this.store.select(FirmwareUpdateSelectors.selectIsUpdateAvailable);
  missingBusTopology$ = this.store.select(FirmwareUpdateSelectors.selectMissingBusTopology);
  versionListParams$ = this.store.select(FirmwareUpdateSelectors.selectVersionListParams);
  salesForceCaseNumber$ = this.store.select(FirmwareUpdateSelectors.selectSalesForceCaseNumber);
  xota2OperationProcess$ = this.store.select(FirmwareUpdateSelectors.selectXotaOperationStatus);
  firmwareUpdateStatusStartConstraints$ = this.store.select(
    FirmwareUpdateSelectors.selectFirmwareUpdateStatusStartConstraints()
  );
  showFirmwareUpdateStart$ = this.store.select(
    FirmwareUpdateSelectors.selectShowFirmwareUpdateStart()
  );

  constructor(private store: Store, private dialog: MatDialog) {}

  setStep(step: FirmwareUpdateStep): void {
    this.store.dispatch(FirmwareUpdateActions.setFirmwareUpdateStep({ step }));
  }

  setBundleUpdateStep(bundleUpdateStep: XotaBundleUpdateStep): void {
    this.store.dispatch(FirmwareUpdateActions.setXotaBundleUpdateStep({ bundleUpdateStep }));
  }

  onCloseModal(): void {
    this.store.dispatch(FirmwareUpdateActions.onCloseFirmwareUpdateModal());
  }

  checkUpdateTimeBlock(): void {
    this.store.dispatch(FirmwareUpdateActions.checkUpdateTimeBlock());
  }

  checkUpdateVerification(): void {
    this.store.dispatch(FirmwareUpdateActions.checkUpdateVerification());
  }

  checkOverallProgress(): void {
    this.store.dispatch(FirmwareUpdateActions.setOverallProgress());
  }

  checkOverallProgressFirmwareUpdate(): void {
    this.store.dispatch(FirmwareUpdateActions.setOverallProgressFirmwareUpdate());
  }

  triggerUpdateFirmwareProcess(): void {
    this.store.dispatch(FirmwareUpdateActions.triggerUpdateFirmware());
  }

  clearLocalStorageKey(gatewaySerial: string): void {
    this.store.dispatch(FirmwareUpdateActions.clearLocalStorageKey({ gatewaySerial }));
  }

  saveLocalStorageFirmwareUpdateState(gatewaySerial: string): void {
    this.store.dispatch(FirmwareUpdateActions.saveFirmwareUpdateStateToLocal({ gatewaySerial }));
  }

  openFirmwareUpdateModalAction(gatewaySerial: string): void {
    this.store.dispatch(FirmwareUpdateActions.openFirmwareUpdateModal({ gatewaySerial }));
  }

  openOneTimeServiceApprovalModal(gatewaySerial: string): void {
    this.store.dispatch(FirmwareUpdateActions.openOneTimeServiceApproval({ gatewaySerial }));
  }

  checkOneTimeServiceApprovalForCheckingProcess(salesForceCaseNumber: string): void {
    this.store.dispatch(
      FirmwareUpdateActions.checkingSalesForceCodeForCheckProcess({ salesForceCaseNumber })
    );
  }

  checkOneTimeServiceApprovalForUpdateProcess(salesForceCaseNumber: string): void {
    this.store.dispatch(
      FirmwareUpdateActions.checkingSalesForceCodeForUpdateProcess({ salesForceCaseNumber })
    );
  }

  closeFirmwareUpdateModalAction(): void {
    this.store.dispatch(FirmwareUpdateActions.onCloseFirmwareUpdateModal());
  }

  openFirmwareUpdateModal(gatewaySerial: string): void {
    const firmwareUpdateDialogRef: MatDialogRef<FirmwareUpdateModalComponent> = this.dialog.open(
      FirmwareUpdateModalComponent,
      {
        width: '710px',
        panelClass: 'firmware-update-dialog',
        disableClose: true,
        autoFocus: false,
        data: {
          gatewaySerial,
        },
      }
    );

    firmwareUpdateDialogRef.afterClosed().subscribe(() => {
      this.onCloseModal();
    });
  }

  verifyGateway(): void {
    this.store.dispatch(FirmwareUpdateActions.verifyGateway.attempt());
  }
}
